import React, {ChangeEvent, useContext, useEffect, useState} from "react";
import Post, {PostContent} from "../models/post";
import AddPostContentModal from "../views/add-post-content-modal";
import Divider from "../components/divider";
import TextInput from "../components/text-input";
import Row from "../components/row";
import {useRecoilValue} from "recoil";
import userState from "../utils/user-state";
import PrimaryButton from "../components/primary-button";
import SpaceBetween from "../components/space-between";
import Textarea from "../components/textarea";
import {nanoid} from "nanoid";
import FirebaseContext from "../utils/firebase-context";
import {useHistory, useParams} from "react-router-dom";
import ContentComponent from "../views/content-component";
import {
    AddContentButton,
    Input,
    Label,
    Placeholder,
    SubTopBar,
    TitleImage,
    TitleInput,
    Toggle,
    TopBarContent
} from "../views/post-components";
import { LinkListComponent } from "../views/link-list-component";
import { Loading } from "../views/loading";
import { AddRounded } from "@mui/icons-material";

export default function CreatePostScreen() {
    const [showModal, setShowModal] = useState(false);
    const [content, setContent] = useState<PostContent[]>([]);
    const [activeIndex, setActiveIndex] = useState(0);
    const user = useRecoilValue(userState);
    const [title, setTitle] = useState("");
    const [author, setAuthor] = useState(user ? user.firstName + " " + user.lastName : "");
    const [image, setImage] = useState<File>();
    const [excerpt, setExcerpt] = useState("");
    const [time, setTime] = useState("");
    const firebase = useContext(FirebaseContext);
    const [sources, setSources] = useState<{text: string; link: string}[]>();
    const {handle} = useParams<{ handle: string }>();
    const history = useHistory();

    const [loading, setLoading] = useState(false);

    const closeModal = () => {
        setShowModal(false);
    }

    useEffect(() => {
        if (user) {
            setAuthor(user.firstName + " " + user.lastName);
        }
    }, [user]);

    const addContent = (c: PostContent, index: number) => {
        let copy = [...content];
        copy.splice(index + 1, 0, c);
        setContent(copy);
        closeModal();
    };

    const deleteContent = (index: number) => () => {
        let copy = [...content];
        copy.splice(index, 1);
        setContent(copy);
    }

    const openModal = (index: number) => {
        setActiveIndex(index);
        setShowModal(true);
    };

    const addContentBefore = (index: number) => () => {
        openModal(index-1);
    };
    const addContentAfter = (index: number) => () => {
        openModal(index);
    };

    const [published, setPublished] = useState(false);

    const updateTitleImage = (e: ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || !files[0]) return;
        setImage(files[0]);
    };

    const createPost = async () => {
        if (!image) return;
        setLoading(true);
        const post: Post = {
            title,
            author,
            excerpt,
            content,
            published,
            time,
            blog: handle,
            sources: sources || [],
            id: nanoid(16)
        }
        await firebase.uploadBlogPost(post, image);
        history.push(`/blogs/${handle}/${post.id}`);
        setLoading(false);
    }

    return <>
        <SubTopBar>
            <TopBarContent>
                <TitleInput value={title} onChange={e => setTitle(e.target.value)} placeholder="Titel..."/>
                <Toggle
                    value={published}
                    onClick={() => setPublished(p => !p)}
                    label="Veröffentlicht?"/>
            </TopBarContent>
        </SubTopBar>
        <div style={{height: "70px"}}/>
        <div style={{
            display: "grid",
            gap: "1rem",
        }}>
            <TitleImage>
                {image ? <img src={URL.createObjectURL(image)} alt=""/> : <Placeholder>
                    <AddRounded />
                </Placeholder>}
                <Input type="file" id={`title-image-input`} onChange={updateTitleImage} accept={"image/*"}/>
                <Label htmlFor={`title-image-input`}/>
            </TitleImage>
            <Row>
                <Textarea value={excerpt} placeholder="Zusammenfassung" onChange={e => setExcerpt(e.target.value)}/>
            </Row>
            <Row>
                <TextInput value={author} onChange={e => setAuthor(e.target.value)} placeholder="Autor"/>
                <TextInput value={time} onChange={e => setTime(e.target.value)} placeholder="Lesedauer in Minuten"/>
            </Row>
            <Divider/>
            {content.map((c, i) => {
                return <ContentComponent
                    key={i}
                    content={c}
                    index={i}
                    setContent={setContent}
                    deleteContent={deleteContent(i)}
                    onAfterAdd={addContentAfter(i)}
                    onBeforeAdd={addContentBefore(i)}
                />
            })}
        </div>

        <AddContentButton onClick={() => openModal(content.length - 1)}>
            Inhalt hinzufügen
        </AddContentButton>
        {!sources ? <AddContentButton onClick={() => setSources([])}>
            Quellen hinzufügen
        </AddContentButton> : <div style={{
                marginTop: "1rem"
            }}
        >
            <LinkListComponent
                deleteContent={() => setSources(undefined)}
                update={sources => setSources(sources)}
                linkList={{items: sources!}}
            />
        </div>}
        <SpaceBetween style={{marginTop: "1rem"}}>
            <div/>
            <PrimaryButton onClick={createPost}>
                Speichern
            </PrimaryButton>
        </SpaceBetween>

        <div style={{height: "10vh"}}/>
        {showModal && <AddPostContentModal
          addContent={addContent}
          close={closeModal}
          index={activeIndex}
        />}
        {loading && <Loading />}
    </>
}